// src/contexts/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
//const API_BASE = 'http://localhost:8060'
//const API_BASE = 'https://servicios.algoritmojuridico.com';

// Use /api prefix in development (localhost) to work with proxy
// In production, use the full backend URL
const API_BASE = typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? '/api'  // Development: proxy will forward to backend
    : 'https://servicios.algoritmojuridico.com';  // Production: direct backend call

interface User {
    username: string;
    email?: string;
    roles: string[];
}

interface AuthContextType {
    user: User | null;
    token: string | null;
    login: (username: string, password: string) => Promise<void>;
    logout: () => void;
    isAuthenticated: boolean;
    isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // Cargar token del localStorage al iniciar
    useEffect(() => {
        const storedToken = localStorage.getItem('auth_token');
        const storedUser = localStorage.getItem('auth_user');

        if (storedToken && storedUser) {
            setToken(storedToken);
            setUser(JSON.parse(storedUser));
        }
        setIsLoading(false);
    }, []);

    const login = async (username: string, password: string) => {
        try {
            const response = await fetch(`${API_BASE}/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: username, password: password }),
            });

            if (!response.ok) {
                throw new Error('Credenciales inválidas');
            }

            const data = await response.json();
            // Validar que el usuario tenga el rol de admin
            if (!data.roles || !Array.isArray(data.roles)) {
                throw new Error('Respuesta del servidor inválida');
            }

            if (!data.roles.includes('admin')) {
                throw new Error('Acceso denegado: Se requiere rol de administrador');
            }

            // Extraer token y crear objeto de usuario
            const authToken = data.access_token;
            const userData: User = {
                username: username,
                email: username,
                roles: data.roles
            };

            setToken(authToken);
            setUser(userData);

            localStorage.setItem('auth_token', authToken);
            localStorage.setItem('auth_user', JSON.stringify(userData));
        } catch (error) {
            console.error('Login error:', error);
            throw error;
        }
    };

    const logout = () => {
        setToken(null);
        setUser(null);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
    };

    const value: AuthContextType = {
        user,
        token,
        login,
        logout,
        isAuthenticated: !!token,
        isLoading,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
